import { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";
import { FileText, Upload, X } from "lucide-react";
import { z } from "zod";

import { cn } from "@/lib/utils";
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from "@/components/ui/attachment";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { BaseForm, FormInput, FormTextarea } from "components/BaseForm";
import { REQUIRED_MARK } from "components/BaseForm/fields/fieldClasses";
import UploadManager from "components/UploadManager";
import { useSaveEmailAttachment } from "hooks/useEmailAttachment";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // must match the backend limit
const FILE_INPUT_ID = "email-attachment-file";

const schema = z.object({
  name: z.string().trim().min(1, "Email Attachment Name is required"),
  file_name: z.string().trim().min(1, "File Name is required"),
  file_extension: z.string(),
  description: z.string().trim().min(1, "Description is required"),
  uploaded_file: z
    .any()
    .refine(file => !!file, "Please select and upload a file first"),
});

/**
 * Splits a filename into its base name and extension (including the dot). A
 * leading dot marks a hidden file, not an extension.
 */
const extractFileNameParts = filename => {
  const lastDotIndex = filename ? filename.lastIndexOf(".") : -1;
  if (lastDotIndex <= 0) {
    return { baseName: filename ?? "", extension: "" };
  }
  return {
    baseName: filename.substring(0, lastDotIndex),
    extension: filename.substring(lastDotIndex),
  };
};

/**
 * The detail endpoint returns the stored file's S3 key only, so size and
 * content type fall back to placeholders.
 */
const toStoredFile = attachment => {
  const s3Key = attachment.attachments?.[0]?.s3_key;
  if (!s3Key) return null;
  return {
    s3_key: s3Key,
    file_name: attachment.file_name,
    file_size: 0,
    file_content_type: "application/octet-stream",
  };
};

const describeRejection = ([rejection]) => {
  const [error] = rejection.errors;
  return error.code === "file-too-large"
    ? "The file exceeds the 25 MB limit."
    : error.message;
};

const AttachmentDropzone = () => {
  const {
    setValue,
    formState: { errors },
  } = useFormContext();
  const uploadedFile = useWatch({ name: "uploaded_file" });
  const [upload, setUpload] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const uploadManager = useRef(new UploadManager());

  useEffect(() => {
    const manager = uploadManager.current;
    const progressId = manager.addProgressListener(([current]) => {
      if (current?.status === "failed") {
        setUploadError(`Upload failed: ${current.error}`);
        manager.removeUploads();
        return;
      }
      setUpload(current ?? null);
    });
    const successId = manager.addSuccessListener(({ attachment }) => {
      setValue("uploaded_file", attachment, { shouldValidate: true });
      manager.removeUploads();
    });

    return () => {
      manager.removeProgressListener(progressId);
      manager.removeSuccessListener(successId);
      manager.removeUploads();
    };
  }, [setValue]);

  const handleDrop = ([file]) => {
    if (!file) return;
    const { baseName, extension } = extractFileNameParts(file.name);
    setValue("file_name", baseName, { shouldValidate: true });
    setValue("file_extension", extension);
    setValue("uploaded_file", null);
    setUploadError("");
    uploadManager.current.removeUploads();
    uploadManager.current.uploadFiles([file]);
  };

  const handleRemove = () => {
    uploadManager.current.removeUploads();
    setValue("uploaded_file", null);
    setValue("file_name", "");
    setValue("file_extension", "");
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: rejections => setUploadError(describeRejection(rejections)),
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  const errorMessage = uploadError || errors.uploaded_file?.message;

  return (
    <Field data-invalid={errorMessage ? true : undefined}>
      <FieldLabel htmlFor={FILE_INPUT_ID} className={REQUIRED_MARK}>
        Select a File
      </FieldLabel>
      <div
        {...getRootProps({
          className: cn(
            "tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:gap-2 tw:rounded-lg tw:border-2 tw:border-dashed tw:border-(--color-border) tw:bg-(--color-surface) tw:p-6 tw:text-center tw:text-sm tw:text-(--color-text-secondary) tw:transition-colors tw:hover:border-(--color-highlight)",
            isDragActive && "tw:border-(--color-highlight)"
          ),
        })}
      >
        <input {...getInputProps({ id: FILE_INPUT_ID })} />
        <Upload className="tw:size-6" />
        <span>Drag and drop a file here, or click to select a file</span>
      </div>

      {upload && (
        <Attachment state="uploading">
          <AttachmentMedia>
            <Spinner />
          </AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle>{upload.file.name}</AttachmentTitle>
            <AttachmentDescription>{`Uploading ${upload.percent}%`}</AttachmentDescription>
          </AttachmentContent>
        </Attachment>
      )}

      {!upload && uploadedFile && (
        <Attachment>
          <AttachmentMedia>
            <FileText />
          </AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle>{uploadedFile.file_name}</AttachmentTitle>
            <AttachmentDescription>
              {uploadedFile.file_size
                ? `${(uploadedFile.file_size / 1024).toFixed(1)} KB`
                : "File uploaded"}
            </AttachmentDescription>
          </AttachmentContent>
          <AttachmentActions>
            <AttachmentAction aria-label="Remove file" onClick={handleRemove}>
              <X />
            </AttachmentAction>
          </AttachmentActions>
        </Attachment>
      )}

      <FieldError>{errorMessage}</FieldError>
    </Field>
  );
};

const FileNameInput = () => {
  const extension = useWatch({ name: "file_extension" });
  return (
    <FormInput
      name="file_name"
      label={`File Name${extension ? ` (Extension: ${extension})` : ""}`}
      placeholder="Enter file name (extension will be preserved)"
      required
    />
  );
};

const EmailAttachmentForm = ({
  attachmentId = null,
  isClone = false,
  initialData = {},
  onSuccess,
  onCancel,
}) => {
  const { mutateAsync: saveAttachment } = useSaveEmailAttachment();
  const isEdit = !!attachmentId && !isClone;

  const storedFile = useMemo(() => toStoredFile(initialData), [initialData]);

  const defaultValues = useMemo(() => {
    const { baseName, extension } = extractFileNameParts(initialData.file_name);
    return {
      // A clone starts unnamed so the user is forced to pick a distinct name.
      name: isClone ? "" : (initialData.name ?? ""),
      file_name: baseName,
      file_extension: extension,
      description: initialData.description ?? "",
      uploaded_file: storedFile,
    };
  }, [initialData, isClone, storedFile]);

  const handleSubmit = async values => {
    const data = {
      name: values.name.trim(),
      file_name: values.file_name.trim() + values.file_extension,
      description: values.description.trim(),
    };
    // An edit that keeps the stored file omits `attachments`, which leaves the
    // stored attachment row untouched rather than replacing it with a copy.
    const keepsStoredFile =
      isEdit && values.uploaded_file.s3_key === storedFile?.s3_key;
    if (!keepsStoredFile) {
      data.attachments = [values.uploaded_file];
    }
    await saveAttachment({ id: isEdit ? attachmentId : null, data });
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      onCancel={onCancel}
    >
      <Card>
        <CardContent>
          <div className="tw:flex tw:flex-col tw:gap-5">
            <FormInput
              name="name"
              label="Email Attachment Name"
              placeholder="Enter attachment name"
              required
            />
            <FileNameInput />
            <AttachmentDropzone />
            <FormTextarea
              name="description"
              label="Description"
              placeholder="Enter description"
              rows={4}
              required
            />
          </div>
        </CardContent>
      </Card>
    </BaseForm>
  );
};

export default EmailAttachmentForm;
